/**
 * Achievement and Unlock Utilities
 * 
 * Provides functions to check and unlock achievements, themes, and avatars
 * based on user progress. These are pure functions that can be easily tested.
 * 
 * @module achievementUtils
 */

import {
  ACHIEVEMENT_THRESHOLDS,
  UNLOCK_THRESHOLDS,
  SCORE_THRESHOLDS,
  TIME_THRESHOLDS
} from '../constants/gameplay';

/**
 * Check for newly unlocked achievements based on progress
 * 
 * @param {Object} newProgress - The updated progress object
 * @param {Array<string>} currentAchievements - Current list of achievement IDs
 * 
 * @returns {Object} Object containing:
 *   - newAchievements: Array of newly unlocked achievement IDs
 *   - updatedProgress: Progress object with achievements added (if any new)
 * 
 * @example
 * const { newAchievements, updatedProgress } = checkAchievements(progress, progress.achievements);
 */
export const checkAchievements = (newProgress, currentAchievements = []) => {
  const newAchievements = [];
  const achievements = newProgress.achievements || currentAchievements;
  
  // First Steps - Complete first question
  if (newProgress.stats.totalQuestionsAnswered >= ACHIEVEMENT_THRESHOLDS.FIRST_QUESTIONS && !achievements.includes('first_steps')) {
    newAchievements.push('first_steps');
  }
  
  // Perfect Score - Get 100% on any subject
  Object.values(newProgress.completedSubjects || {}).forEach(subject => {
    if (subject.score === SCORE_THRESHOLDS.PERFECT && !achievements.includes('perfect_score')) {
      newAchievements.push('perfect_score');
    }
  });
  
  // Subject Master - Complete all questions in a subject
  const completedSubjects = Object.keys(newProgress.completedSubjects || {}).length;
  if (completedSubjects >= ACHIEVEMENT_THRESHOLDS.SUBJECT_MASTER && !achievements.includes('subject_master')) {
    newAchievements.push('subject_master');
  }
  
  // Scholar - Complete 5 different subjects
  if (completedSubjects >= ACHIEVEMENT_THRESHOLDS.SCHOLAR && !achievements.includes('scholar')) {
    newAchievements.push('scholar');
  }
  
  // Perfectionist - Get perfect scores on 3 subjects
  const perfectScores = Object.values(newProgress.completedSubjects || {}).filter(s => s.score === SCORE_THRESHOLDS.PERFECT).length;
  if (perfectScores >= ACHIEVEMENT_THRESHOLDS.PERFECTIONIST && !achievements.includes('perfectionist')) {
    newAchievements.push('perfectionist');
  }
  
  // Marathon Runner - Answer 50 questions in one session
  if (newProgress.stats.totalQuestionsAnswered >= ACHIEVEMENT_THRESHOLDS.MARATHON_RUNNER && !achievements.includes('marathon_runner')) {
    newAchievements.push('marathon_runner');
  }
  
  // Time-based achievements
  const now = new Date();
  const hour = now.getHours();
  if (hour >= TIME_THRESHOLDS.EARLY_BIRD_START && hour < TIME_THRESHOLDS.EARLY_BIRD_END && !achievements.includes('early_bird')) {
    newAchievements.push('early_bird');
  }
  if (hour >= TIME_THRESHOLDS.NIGHT_OWL_START && !achievements.includes('night_owl')) {
    newAchievements.push('night_owl');
  }
  
  // Subject-specific achievements
  if (newProgress.completedSubjects?.spelling && !achievements.includes('artist')) {
    newAchievements.push('artist');
  }
  if (newProgress.completedSubjects?.science && !achievements.includes('scientist')) {
    newAchievements.push('scientist');
  }
  if (newProgress.completedSubjects?.math && !achievements.includes('mathematician')) {
    newAchievements.push('mathematician');
  }
  if (newProgress.completedSubjects?.reading && !achievements.includes('reader')) {
    newAchievements.push('reader');
  }
  if (newProgress.completedSubjects?.social && !achievements.includes('citizen')) {
    newAchievements.push('citizen');
  }
  if (newProgress.completedSubjects?.skipcounting && !achievements.includes('counter')) {
    newAchievements.push('counter');
  }
  if (newProgress.completedSubjects?.phonics && !achievements.includes('phonics_master')) {
    newAchievements.push('phonics_master');
  }
  
  // Grand Master - Complete ALL subjects with perfect scores
  const allSubjects = ['phonics', 'math', 'reading', 'spelling', 'science', 'social', 'skipcounting', 'art', 'geography', 'history'];
  const allPerfect = allSubjects.every(subject => 
    newProgress.completedSubjects?.[subject] && newProgress.completedSubjects[subject].score === SCORE_THRESHOLDS.PERFECT
  );
  if (allPerfect && !achievements.includes('grand_master')) {
    newAchievements.push('grand_master');
  }
  
  // Update progress with new achievements
  if (newAchievements.length > 0) {
    const updatedProgress = {
      ...newProgress,
      achievements: [...achievements, ...newAchievements]
    };
    
    return {
      newAchievements,
      updatedProgress
    };
  }
  
  return {
    newAchievements: [],
    updatedProgress: newProgress
  };
};

/**
 * Check and unlock themes/avatars based on progress
 * 
 * @param {Object} newProgress - The updated progress object
 * 
 * @returns {Object} Progress object with unlocked themes and avatars added
 * 
 * @example
 * const updatedProgress = checkUnlocks(progress);
 */
export const checkUnlocks = (newProgress) => {
  const unlockedThemes = [...(newProgress.unlockedThemes || ['default'])];
  const unlockedAvatars = [...(newProgress.unlockedAvatars || ['default'])];
  
  // Theme unlocks
  const completedSubjects = Object.keys(newProgress.completedSubjects || {}).length;
  if (completedSubjects >= UNLOCK_THRESHOLDS.OCEAN_THEME && !unlockedThemes.includes('ocean')) {
    unlockedThemes.push('ocean');
  }
  
  const perfectScores = Object.values(newProgress.completedSubjects || {}).filter(s => s.score === SCORE_THRESHOLDS.PERFECT).length;
  if (perfectScores >= UNLOCK_THRESHOLDS.FOREST_THEME && !unlockedThemes.includes('forest')) {
    unlockedThemes.push('forest');
  }
  
  if (newProgress.stats?.totalQuestionsAnswered >= UNLOCK_THRESHOLDS.SPACE_THEME && !unlockedThemes.includes('space')) {
    unlockedThemes.push('space');
  }
  
  // Avatar unlocks
  if (completedSubjects >= UNLOCK_THRESHOLDS.ROBOT_AVATAR && !unlockedAvatars.includes('robot')) {
    unlockedAvatars.push('robot');
  }
  if (perfectScores >= UNLOCK_THRESHOLDS.PRINCESS_AVATAR && !unlockedAvatars.includes('princess')) {
    unlockedAvatars.push('princess');
  }
  if (newProgress.stats?.totalQuestionsAnswered >= UNLOCK_THRESHOLDS.SUPERHERO_AVATAR && !unlockedAvatars.includes('superhero')) {
    unlockedAvatars.push('superhero');
  }
  if (completedSubjects >= UNLOCK_THRESHOLDS.ASTRONAUT_AVATAR && !unlockedAvatars.includes('astronaut')) {
    unlockedAvatars.push('astronaut');
  }
  
  return {
    ...newProgress,
    unlockedThemes,
    unlockedAvatars
  };
};

